import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {
  docHeaderShellSx,
  headerKickerSx,
  headerTitleSx,
  headerPhasesRowSx,
  sectionTitleRowSx,
  sectionIndexChipBase,
  sectionTitleTextSx,
  SECTION_CHIP_COLORS,
  subsectionTitleSx,
  objectiveSx,
  timeLimitShellSx,
  timeLimitLabelSx,
  boldLabelSx,
  footerSx,
  checklistPageSx,
  checklistUlSx,
  panelTitleSx,
} from "./checklistStyles";
import {
  splitTextWithDeadlines,
  DEADLINE_CHIP_PALETTE,
} from "./deadlineChips";

const NS = "garmentProdChecklist";

const PHASE_CHIP_COLORS = [
  { bg: "rgba(57, 73, 171, 0.12)", color: "#283593" },
  { bg: "rgba(0, 131, 143, 0.12)", color: "#006064" },
  { bg: "rgba(239, 108, 0, 0.12)", color: "#e65100" },
  { bg: "rgba(123, 31, 162, 0.12)", color: "#6a1b9a" },
];

export const ChecklistPage = ({ children }) => (
  <Box
    className="checklist-page"
    sx={{
      ...checklistPageSx,
      display: "flex",
      flexDirection: "column",
    }}
  >
    {children}
  </Box>
);

export const DocHeader = ({ t }) => {
  const phases = t(`${NS}.phaseChips`, { returnObjects: true }) || [];

  return (
    <Box sx={docHeaderShellSx}>
      <Typography component="p" sx={headerKickerSx}>
        {t(`${NS}.headerKicker`)}
      </Typography>
      <Typography component="h1" sx={headerTitleSx}>
        {t(`${NS}.headerTitle`)}
      </Typography>
      {Array.isArray(phases) && phases.length > 0 && (
        <Box sx={headerPhasesRowSx}>
          {phases.map((label, i) => {
            const palette = PHASE_CHIP_COLORS[i % PHASE_CHIP_COLORS.length];
            return (
              <Chip
                key={label}
                label={label}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 700,
                  fontSize: "0.68rem",
                  letterSpacing: 0.2,
                  bgcolor: palette.bg,
                  color: palette.color,
                  border: `1px solid ${palette.color}22`,
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export const DocFooter = ({ t, page, total = 4 }) => (
  <Box sx={footerSx}>
    <span>{t(`${NS}.footerManual`)}</span>
    <span>{t(`${NS}.pageOf`, { page, total })}</span>
  </Box>
);

const sectionNumberFromTitle = (title) => {
  const m = String(title).match(/^(\d+)/);
  return m ? m[1] : null;
};

export const SectionTitle = ({ children, sectionKey }) => {
  const num = sectionNumberFromTitle(children);
  const chipColors =
    SECTION_CHIP_COLORS[sectionKey] || SECTION_CHIP_COLORS.default;

  return (
    <Box sx={sectionTitleRowSx}>
      {num && (
        <Chip
          label={num}
          size="small"
          sx={{
            ...sectionIndexChipBase,
            bgcolor: chipColors.bg,
            color: chipColors.color,
            border: `1px solid ${chipColors.color}33`,
          }}
        />
      )}
      <Typography component="h2" sx={sectionTitleTextSx}>
        {children}
      </Typography>
    </Box>
  );
};

export const SubsectionTitle = ({ children }) => (
  <Typography component="h3" sx={subsectionTitleSx}>
    {children}
  </Typography>
);

export const DeadlineChip = ({ label, deadlineType = "days" }) => {
  const palette =
    DEADLINE_CHIP_PALETTE[deadlineType] || DEADLINE_CHIP_PALETTE.days;

  return (
    <Chip
      component="span"
      label={label}
      size="small"
      icon={<ScheduleIcon sx={{ fontSize: "14px !important" }} />}
      sx={{
        height: 22,
        mx: 0.35,
        verticalAlign: "middle",
        fontWeight: 800,
        fontSize: "0.68rem",
        bgcolor: palette.bg,
        color: palette.main,
        border: `1px solid ${palette.main}33`,
        "& .MuiChip-icon": { color: palette.main, ml: 0.5 },
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
};

export const TextWithDeadlineChips = ({ text }) => {
  const parts = splitTextWithDeadlines(text);

  if (parts.length === 1 && parts[0].type === "text") {
    return text;
  }

  return (
    <>
      {parts.map((part, i) =>
        part.type === "deadline" ? (
          <DeadlineChip
            key={`${i}-${part.value}`}
            label={part.value}
            deadlineType={part.deadlineType}
          />
        ) : (
          <Box component="span" key={`${i}-t`}>
            {part.value}
          </Box>
        ),
      )}
    </>
  );
};

export const ObjectiveBox = ({ t, labelKey, text }) => (
  <Box sx={objectiveSx}>
    <strong>{t(`${NS}.${labelKey}`)}</strong>
    <TextWithDeadlineChips text={text} />
  </Box>
);

const splitTimeLimitText = (text) => {
  const match = text.match(/[:：]/);
  if (!match || match.index == null) {
    return { label: null, body: text };
  }
  return {
    label: text.slice(0, match.index).trim(),
    body: text.slice(match.index + 1).trim(),
  };
};

export const TimeLimit = ({ children }) => {
  if (typeof children !== "string") {
    return <Box sx={timeLimitShellSx}>{children}</Box>;
  }

  const { label, body } = splitTimeLimitText(children);

  return (
    <Box sx={timeLimitShellSx}>
      <ScheduleIcon sx={{ color: "#c53030", fontSize: 20, flexShrink: 0 }} />
      {label && (
        <Typography component="span" sx={timeLimitLabelSx}>
          {label}:
        </Typography>
      )}
      <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: "10pt" }}>
        <TextWithDeadlineChips text={body} />
      </Box>
    </Box>
  );
};

export const BoldLabel = ({ children }) => (
  <Typography sx={boldLabelSx}>{children}</Typography>
);

export const CheckList = ({ items = [], nestedMap = null }) => (
  <Box component="ul" sx={checklistUlSx}>
    {items.map((item, idx) => {
      const nested = nestedMap?.[idx];
      return (
        <Box component="li" key={idx}>
          <TextWithDeadlineChips text={item} />
          {nested?.length > 0 && (
            <Box component="ul" className="nested">
              {nested.map((sub, subIdx) => (
                <Box component="li" key={subIdx}>
                  <TextWithDeadlineChips text={sub} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

export const PresentationRow = ({ text, mark, variant }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 1,
      py: 0.65,
      borderBottom: "1px solid",
      borderColor: variant === "ok" ? "#c6f6d5" : "#fed7d7",
      fontSize: "10pt",
    }}
  >
    <span>{text}</span>
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        color: variant === "ok" ? "#2f855a" : "#c53030",
        flexShrink: 0,
      }}
    >
      {variant === "ok" ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
      ) : (
        <CancelOutlinedIcon sx={{ fontSize: 18 }} />
      )}
    </Box>
  </Box>
);

export const PanelTitle = ({ variant, children }) => (
  <Typography sx={panelTitleSx(variant === "ok" ? "#22543d" : "#742a2a")}>
    {variant === "ok" ? (
      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#38a169" }} />
    ) : (
      <CancelOutlinedIcon sx={{ fontSize: 18, color: "#e53e3e" }} />
    )}
    {children}
  </Typography>
);
